import { Schema } from "effect"
import { AgentKind, Effort, Owned } from "./common"
import { Millis, NonNegInt, PosInt, ProfileId, ProjectId, RepoId, RunnerId, Sha256, TaskId } from "./ids"
import { Labels, Os, ToolCap, Trust } from "./placement"

// #region runner
/** Readiness check per profile (Paperclip's testEnvironment), shown on the Runners screen. */
export const ProfileReadiness = Schema.TaggedUnion({
  ready: {},
  logged_out: {},
  cli_missing: {},
  cli_too_old: { found: Schema.String, minimum: Schema.String },
  quota_exhausted: { resetsAt: Schema.NullOr(Millis) },
  error: { message: Schema.String },
  /** enabled = false or capacity 0 in runner.toml (console): advertised, never offered; maxConcurrent stays ≥ 1. */
  disabled: {},
})

/** Remaining quota as the CLI reports it (Claude rate_limit_event, Codex account/rateLimits). Never a credential. */
export const QuotaWindow = Schema.Struct({
  window: Schema.Literals(["5h", "24h", "7d", "other"]),
  usedPercent: Schema.Number,
  resetsAt: Schema.NullOr(Millis),
})

/** Claude Agent SDK `PermissionMode` (0.3.295; `default` | `acceptEdits` | `bypassPermissions` | `plan` | `dontAsk` | `auto`). */
export const ClaudePermissionMode = Schema.Literals([
  "default",
  "acceptEdits",
  "bypassPermissions",
  "plan",
  "dontAsk",
  "auto",
])
/** Codex app-server v2 `SandboxMode`. */
export const CodexSandboxMode = Schema.Literals(["read-only", "workspace-write", "danger-full-access"])
/** Codex app-server v2 `AskForApproval` (the string forms; `granular` is not offered). */
export const CodexApprovalPolicy = Schema.Literals(["untrusted", "on-request", "never"])

export type ClaudePermissionMode = typeof ClaudePermissionMode.Type
export type CodexSandboxMode = typeof CodexSandboxMode.Type
export type CodexApprovalPolicy = typeof CodexApprovalPolicy.Type

/** Decision 0020: the access mode of a runner profile, mirroring each CLI's own choices. Default "auto". */
export const AccessMode = Schema.Literals(["auto", "ask", "full"])
export type AccessMode = typeof AccessMode.Type

/** What each access mode passes to each CLI (pure data, shared by runner and web; decision 0020's table). */
export const ACCESS_MODE_PRESETS = {
  auto: { claudePermissionMode: "auto", codexSandbox: "workspace-write", codexApprovalPolicy: "on-request" },
  ask: { claudePermissionMode: "default", codexSandbox: "read-only", codexApprovalPolicy: "on-request" },
  full: {
    claudePermissionMode: "bypassPermissions",
    codexSandbox: "danger-full-access",
    codexApprovalPolicy: "never",
  },
} as const satisfies Record<
  AccessMode,
  {
    claudePermissionMode: ClaudePermissionMode
    codexSandbox: CodexSandboxMode
    codexApprovalPolicy: CodexApprovalPolicy
  }
>

/** MCP servers a run loads: the CLI's own plus Minga's (default "cli"), or Minga's only (runner.toml escape hatch). */
export const RunMcpMode = Schema.Literals(["cli", "minga_only"])
export type RunMcpMode = typeof RunMcpMode.Type

/**
 * Under Auto: the user's MCP servers and connectors run without asking ("approve", default) or follow the CLI's own
 * approval rules ("ask"). Ask and Full ignore it (decision 0024).
 */
export const McpApproval = Schema.Literals(["approve", "ask"])
export type McpApproval = typeof McpApproval.Type

/**
 * A per-run request that can only narrow the profile (decision 0026). This wave: only the MCP server set. Absent = as
 * the profile says. `"minga_only"` narrows; `"cli"` is neutral (kept only in `Mind.runner.access`, where it records
 * that a person switched outside tools on). New fields need a new `RUNNER_FEATURES` entry (settings wave §4.4).
 */
export const AccessRequest = Schema.Struct({
  mcp: Schema.optionalKey(RunMcpMode),
})
export type AccessRequest = typeof AccessRequest.Type

/** A model id as a profile override or a preset names it. */
const ModelOverride = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128))

/**
 * A runner profile's CLI permission settings, as configured in the runner console and `runner.toml` (decision 0020,
 * fix wave 2026-10-09 §4.1). `accessMode` picks the preset; the three mode-driven fields are **advanced overrides**:
 * null = take it from the access mode (`ACCESS_MODE_PRESETS`). `claudeSandbox` keeps its meaning (null = pass
 * nothing). Use `effectiveCli` for what a run actually passes. Minga adds no wrapper on top.
 */
export const ProfileCliSettings = Schema.Struct({
  /** Claude `permissionMode` override. */
  claudePermissionMode: Schema.NullOr(ClaudePermissionMode),
  /** Claude Code sandbox on/off (SDK `sandbox.enabled`); null = pass nothing. */
  claudeSandbox: Schema.NullOr(Schema.Boolean),
  /** Codex `sandbox` override. */
  codexSandbox: Schema.NullOr(CodexSandboxMode),
  /** Codex `approvalPolicy` override. */
  codexApprovalPolicy: Schema.NullOr(CodexApprovalPolicy),
  /** Absent only from a runner that predates decision 0020; the runner always fills it. */
  accessMode: Schema.optionalKey(AccessMode),
  /** Absent = "cli". */
  mcp: Schema.optionalKey(RunMcpMode),
  /** Settings wave (decision 0024): Auto only. Absent = "approve" (runners before 0.3.0). */
  mcpApproval: Schema.optionalKey(McpApproval),
  /** Codex, Auto only: commands can reach the network. Absent = true. */
  autoNetwork: Schema.optionalKey(Schema.Boolean),
  /** Minga's per-profile model for runs whose job names none. null/absent = the CLI default (console `defaultModel`). */
  modelOverride: Schema.optionalKey(Schema.NullOr(ModelOverride)),
  /** Effort for runs whose job names none. null/absent = the CLI default. */
  effortOverride: Schema.optionalKey(Schema.NullOr(Effort)),
})
export type ProfileCliSettings = typeof ProfileCliSettings.Type

/**
 * The console's wish for `ProfileCliSettings`: per field, absent = leave as configured on the runner, null = unset
 * (back to the access mode's value), a value = set it. `accessMode` has no null: set "auto" to go back to the default.
 */
export const ProfileCliWish = Schema.Struct({
  claudePermissionMode: Schema.optionalKey(Schema.NullOr(ClaudePermissionMode)),
  claudeSandbox: Schema.optionalKey(Schema.NullOr(Schema.Boolean)),
  codexSandbox: Schema.optionalKey(Schema.NullOr(CodexSandboxMode)),
  codexApprovalPolicy: Schema.optionalKey(Schema.NullOr(CodexApprovalPolicy)),
  accessMode: Schema.optionalKey(AccessMode),
  /** Settings wave (runner 0.3.0, feature `cli_settings_v2`): absent = leave; null clears where nullable. */
  mcp: Schema.optionalKey(RunMcpMode),
  mcpApproval: Schema.optionalKey(McpApproval),
  autoNetwork: Schema.optionalKey(Schema.Boolean),
  modelOverride: Schema.optionalKey(Schema.NullOr(ModelOverride)),
  effortOverride: Schema.optionalKey(Schema.NullOr(Effort)),
})
export type ProfileCliWish = typeof ProfileCliWish.Type

/** The wish keys only a runner with the feature `cli_settings_v2` (0.3.0) understands (H18 refuses them otherwise). */
export const CLI_SETTINGS_V2_KEYS = [
  "mcp",
  "mcpApproval",
  "autoNetwork",
  "modelOverride",
  "effortOverride",
] as const

/** What a run passes to its CLI, computed the same way by runner and web. */
export interface EffectiveCli {
  readonly accessMode: AccessMode
  readonly claudePermissionMode: ClaudePermissionMode
  readonly claudeSandbox: boolean | null
  readonly codexSandbox: CodexSandboxMode
  readonly codexApprovalPolicy: CodexApprovalPolicy
  readonly mcp: RunMcpMode
  /** Any of the three mode-driven fields is overridden. */
  readonly custom: boolean
  /** Under Auto: MCP tools run without asking ("approve") or as the CLI asks. Ask and Full ignore it. */
  readonly mcpApproval: McpApproval
  /**
   * Codex `sandbox_workspace_write.network_access` when the sandbox is `workspace-write`; null where it does not apply
   * (read-only and danger-full-access sandboxes, and Claude, whose caller ignores it).
   */
  readonly network: boolean | null
  readonly modelOverride: string | null
  readonly effortOverride: Effort | null
}

/** Facts only the runner knows (its profile's `config.toml`), as it reports them in `ConsoleCliSettings.effective`. */
export interface RunnerAccessFacts {
  readonly network?: boolean
  readonly mcpApproval?: McpApproval
  /** User MCP servers whose own config sets their approval mode (left untouched by Auto). */
  readonly ownApproval?: ReadonlyArray<string>
}

/** Each mode-driven field = its override when set, else `ACCESS_MODE_PRESETS[accessMode ?? "auto"]`. */
export const effectiveCli = (c: ProfileCliSettings): EffectiveCli => {
  const accessMode = c.accessMode ?? "auto"
  const preset = ACCESS_MODE_PRESETS[accessMode]
  const codexSandbox = c.codexSandbox ?? preset.codexSandbox
  return {
    accessMode,
    claudePermissionMode: c.claudePermissionMode ?? preset.claudePermissionMode,
    claudeSandbox: c.claudeSandbox,
    codexSandbox,
    codexApprovalPolicy: c.codexApprovalPolicy ?? preset.codexApprovalPolicy,
    mcp: c.mcp ?? "cli",
    custom: c.claudePermissionMode !== null || c.codexSandbox !== null || c.codexApprovalPolicy !== null,
    mcpApproval: c.mcpApproval ?? "approve",
    // network is Minga's to switch only in Auto (decision 0024); other modes keep the CLI's default (off)
    network: codexSandbox === "workspace-write" ? accessMode === "auto" && (c.autoNetwork ?? true) : null,
    modelOverride: c.modelOverride ?? null,
    effortOverride: c.effortOverride ?? null,
  }
}

/**
 * `effectiveCli` plus a run's request and the runner's facts (decision 0026). Pure. The runner calls it with its own
 * facts; the web with the facts from the console manifest's `effective`. Without facts, network and MCP approval
 * follow the settings. A request only narrows: `mcp: "minga_only"` turns a `cli` profile into Minga's servers only.
 */
export const effectiveAccess = (
  c: ProfileCliSettings,
  r: AccessRequest | undefined,
  facts?: RunnerAccessFacts,
): EffectiveCli => {
  const e = effectiveCli(c)
  return {
    ...e,
    mcp: r?.mcp === "minga_only" ? "minga_only" : e.mcp,
    mcpApproval: facts?.mcpApproval ?? e.mcpApproval,
    network: e.network === null ? null : (facts?.network ?? e.network),
  }
}

/** A run's request narrows something (the only case `Run.access` / `JobTemplate.access` / `JobSpec.access` carry it). */
export const narrowsAccess = (r: AccessRequest | undefined | null): r is AccessRequest =>
  r?.mcp === "minga_only"

/** Manifest features and the runner version that introduced each (settings wave §5.2). */
export const RUNNER_FEATURES = { access_request: "0.3.0", cli_settings_v2: "0.3.0" } as const
export type RunnerFeature = keyof typeof RUNNER_FEATURES

/**
 * One CLI login on the machine, with its own home (CODEX_HOME / CLAUDE_CONFIG_DIR). This user's runner:
 * codex-personal (~/.codex, personal workspaces) and codex-every (~/.codex-alt, the Every workspace only).
 * A manifest sent to workspace W lists ONLY the profiles whose local scope includes W.
 */
export const RunnerProfile = Schema.Struct({
  id: ProfileId,
  agent: AgentKind,
  /** Display only ("~/.codex-alt"). The cloud never sends a path back down; jobs name the profile id. */
  homeDisplay: Schema.String,
  cliVersion: Schema.NullOr(Schema.String),
  auth: Schema.Literals(["subscription", "api_key"]),
  /** Project-level scoping inside this workspace; null = every project. */
  projects: Schema.NullOr(Schema.Array(ProjectId)),
  /** Rate limits apply per account, not per machine. */
  maxConcurrent: PosInt,
  readiness: ProfileReadiness,
  quota: Schema.Array(QuotaWindow),
  models: Schema.NullOr(Schema.Array(Schema.String)),
})

export const RunnerRepo = Schema.Struct({
  repoId: RepoId,
  remote: Schema.String, // must match the workspace Repo.remote
  defaultBranch: Schema.String,
  worktrees: Schema.Array(Schema.Struct({ taskId: TaskId, branch: Schema.String, dirty: Schema.Boolean })),
})

/** Summary + hash of the on-device policy ceiling (only the owner, on the device, can raise it). */
export const PolicyCeilingSummary = Schema.Struct({
  hash: Sha256,
  allowedRepos: Schema.Array(RepoId),
  allowedAgents: Schema.Array(AgentKind),
  maxPermission: Schema.Literals(["read_only", "workspace_write", "workspace_write_network", "full_auto"]),
  allowComputerUse: Schema.Boolean,
  allowRunKinds: Schema.Array(Schema.Literals(["turn", "worker", "validator", "publish"])),
})

/** Sent with Register and again whenever it changes; the hub ignores older revisions. */
export const RunnerManifest = Schema.Struct({
  revision: NonNegInt,
  name: Schema.String, // "jannik-mbp"
  mode: Schema.Literals(["service", "one_job"]), // one_job = Cloudflare Sandbox, bound to one run
  trust: Trust,
  platform: Schema.Struct({ os: Os, osVersion: Schema.String, arch: Schema.Literals(["arm64", "x64"]) }),
  versions: Schema.Struct({ minga: Schema.String, bun: Schema.String, protocol: Schema.Int }),
  capacity: PosInt, // machine slots; each run costs 1 in v1
  profiles: Schema.Array(RunnerProfile),
  tools: Schema.Array(ToolCap),
  repos: Schema.Array(RunnerRepo),
  labels: Labels,
  ceiling: PolicyCeilingSummary,
  /** From 0.3.0: what this runner understands (`RUNNER_FEATURES`: "access_request", "cli_settings_v2"). */
  features: Schema.optionalKey(Schema.Array(Schema.String)),
  /** The runner-console v2 manifest (decision 0010). Display only; decoded leniently by the hub's sanitizeManifest. */
  console: Schema.optionalKey(Schema.Json),
})

export const RunnerAdminState = Schema.Literals(["active", "paused", "draining", "revoked"])
/** Derived, never stored: online (connected, fresh heartbeat, free slots) · busy · away (disconnected < 24 h) ·
 * offline · needs_attention (a profile logged out / CLI too old / runner version below minimum). */
export const RunnerPresence = Schema.Literals(["online", "busy", "away", "offline", "needs_attention"])

/**
 * Workspace DO record. A runner identity is one pairing of a machine with ONE workspace; a machine that serves
 * several workspaces holds one control socket (and one RunnerId) per workspace and enforces machine-wide
 * capacity locally. ownerId = owner lock: it only claims runs whose principal is its owner.
 */
export const RunnerRecord = Schema.Struct({
  id: RunnerId,
  ...Owned,
  name: Schema.String,
  admin: RunnerAdminState,
  tokenHash: Sha256, // sha256 of the runner token from device pairing; the token is never stored
  manifest: Schema.NullOr(RunnerManifest),
  incarnation: NonNegInt, // +1 on every Register; offers and claims carry it
  connectedAt: Schema.NullOr(Millis),
  lastHeartbeatAt: Schema.NullOr(Millis),
  createdAt: Millis,
})
export type RunnerRecord = typeof RunnerRecord.Type
// #endregion runner

// #region console
/** Runner console manifest parts shared by cloud, runner and web (moved from the cloud's provisional copy, settings
 * wave §5.2). Display only; the hub's sanitizer rebuilds them from whatever a runner sent. */
const Text = (max: number) => Schema.String.check(Schema.isMaxLength(max))

/** One model a profile's CLI offers (probed). `supportsAutoMode` (Claude, 0.3.0): false = Auto falls back (S5). */
export const ConsoleModelInfo = Schema.Struct({
  id: Text(128),
  label: Schema.optionalKey(Text(128)),
  isDefault: Schema.optionalKey(Schema.Boolean),
  reasoningEfforts: Schema.optionalKey(Schema.Array(Text(32))),
  contextWindow: Schema.optionalKey(Schema.Number),
  supportsAutoMode: Schema.optionalKey(Schema.Boolean),
})
export type ConsoleModelInfo = typeof ConsoleModelInfo.Type

/**
 * A profile's CLI settings as the console manifest reports them: the configured values (`ProfileCliSettings`) plus
 * what runs pass (`effective`, as the runner resolved them, including `config.toml` facts) and whether any override
 * is set (`custom`). 0.3.0 always sends every key. The console `RunnerProfile.defaultModel` (what the CLI itself would
 * use) is a different field from `modelOverride` (Minga's per-profile choice).
 */
export const ConsoleCliSettings = Schema.Struct({
  ...ProfileCliSettings.fields,
  effective: Schema.optionalKey(
    Schema.Struct({
      claudePermissionMode: ClaudePermissionMode,
      codexSandbox: CodexSandboxMode,
      codexApprovalPolicy: CodexApprovalPolicy,
      mcpApproval: Schema.optionalKey(McpApproval),
      network: Schema.optionalKey(Schema.Boolean),
      ownApproval: Schema.optionalKey(Schema.Array(Text(128))),
    }),
  ),
  custom: Schema.optionalKey(Schema.Boolean),
})
export type ConsoleCliSettings = typeof ConsoleCliSettings.Type

/** The runner facts inside a console `effective` (the input `effectiveAccess` takes). */
export const accessFactsOf = (c: ConsoleCliSettings | undefined): RunnerAccessFacts | undefined => {
  const e = c?.effective
  if (!e) return undefined
  return {
    ...(e.network !== undefined ? { network: e.network } : {}),
    ...(e.mcpApproval !== undefined ? { mcpApproval: e.mcpApproval } : {}),
    ...(e.ownApproval !== undefined ? { ownApproval: e.ownApproval } : {}),
  }
}
// #endregion console
